export const SPARKLINE_GREEN = '#13AB5B'
export const SPARKLINE_RED = '#E93323'

export interface FinanceMetricTrends {
  totalRevenue: number[]
  pendingPayments: number[]
  paidInvoices: number[]
  overdueAmount: number[]
}

const CHART_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec',
] as const

export function sliceYearToDateTrend(values: number[]): number[] {
  const endIdx = new Date().getMonth()
  return values.slice(0, endIdx + 1)
}

function filterYearToDateProfitLoss(
  profitLoss: { month: string; loss: number; profit: number }[]
) {
  const endIdx = new Date().getMonth()
  const allowed = new Set(CHART_MONTHS.slice(0, endIdx + 1))
  return profitLoss.filter((d) => allowed.has(d.month as (typeof CHART_MONTHS)[number]))
}

function pickTrendSeries(apiSeries: number[] | undefined, fallback: number[]): number[] {
  const ytdApi = apiSeries ? sliceYearToDateTrend(apiSeries) : []
  const apiUsable = ytdApi.length >= 2 && ytdApi.some((v) => v > 0)
  if (apiUsable) return ytdApi
  if (fallback.length >= 2) return fallback
  if (fallback.length === 1) return [fallback[0], fallback[0]]
  if (ytdApi.length >= 2) return ytdApi
  return ytdApi
}

/**
 * Prefer backend metricTrends; if missing (old API), build month series from yearly profit/loss invoice data.
 */
export function buildMetricTrends(
  api: FinanceMetricTrends | undefined,
  profitLoss: { month: string; loss: number; profit: number }[],
  totals: { totalRevenue: number; paidInvoices: number; pendingPayments: number; overdueAmount: number }
): FinanceMetricTrends {
  const pl = filterYearToDateProfitLoss(profitLoss)
  const revenueMonthly = pl.map((d) => d.profit)
  const revTotal = totals.totalRevenue || revenueMonthly.reduce((a, b) => a + b, 0) || 1
  const paidRatio = Math.min(1, totals.paidInvoices / revTotal)
  const pendingRatio = Math.min(1, totals.pendingPayments / revTotal)
  const overdueRatio = Math.min(1, totals.overdueAmount / revTotal)

  return {
    totalRevenue: pickTrendSeries(api?.totalRevenue, revenueMonthly),
    paidInvoices: pickTrendSeries(api?.paidInvoices, revenueMonthly.map((v) => v * paidRatio)),
    pendingPayments: pickTrendSeries(api?.pendingPayments, pl.map((d) => d.loss * pendingRatio)),
    overdueAmount: pickTrendSeries(api?.overdueAmount, pl.map((d) => d.loss * overdueRatio)),
  }
}

export function prepareSparkline(values: number[] | undefined): number[] | undefined {
  if (!values?.length) return undefined
  const ytd = sliceYearToDateTrend(values)
  if (!ytd.length) return undefined
  if (ytd.length === 1) return [ytd[0], ytd[0]]
  return ytd
}

/**
 * Green/red from the latest month vs the previous month (recent trend).
 * @param invert — true for pending/overdue (down = good).
 */
export function sparklineColor(values: number[] | undefined, invert = false): string {
  const series = prepareSparkline(values)
  if (!series || series.length < 2) return SPARKLINE_GREEN

  const last = series[series.length - 1]
  const prev = series[series.length - 2]
  const rising = last >= prev
  const favourable = invert ? !rising : rising
  return favourable ? SPARKLINE_GREEN : SPARKLINE_RED
}

/** Human-readable month-on-month label for MetricCard trend area. */
export function formatMonthOnMonthTrend(values: number[] | undefined): string {
  const pct = sparklineRecentChangePercent(values)
  if (pct === null) return ''
  const arrow = pct >= 0 ? '↑' : '↓'
  return `${arrow}${Math.abs(pct).toFixed(1)}% MoM`
}

/** Optional % change label for tooltip/display (latest vs previous month). */
export function sparklineRecentChangePercent(values: number[] | undefined): number | null {
  const series = prepareSparkline(values)
  if (!series || series.length < 2) return null
  const last = series[series.length - 1]
  const prev = series[series.length - 2]
  if (prev === 0) return last > 0 ? 100 : 0
  return ((last - prev) / Math.abs(prev)) * 100
}
